import { useQuery } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import { getDocumentation } from "../../common/api/documentationService";
import "./Documentation.scss";
import { useTranslation } from "react-i18next";
import {
  getPrivacyPolicy,
  getTermsAndConditions,
} from "../../common/api/apiCalls";
interface DocumentationData {
  version: string;
  contactEmail: string;
  terms: string[];
}

const Documentation = () => {
  const { data, error, isLoading } = useQuery<DocumentationData>({
    queryKey: ["documentation"],
    queryFn: getDocumentation,
  });

  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"terms" | "privacy">("terms");

  const {
    data: termsResponse,
    isLoading: isLoadingTerms,
    error: termsError,
  } = useQuery({
    queryKey: ["terms-and-conditions"],
    queryFn: getTermsAndConditions,
  });

  const {
    data: privacyResponse,
    isLoading: isLoadingPrivacy,
    error: privacyError,
  } = useQuery({
    queryKey: ["privacy-policy"],
    queryFn: getPrivacyPolicy,
  });

  const termsHtml: string = useMemo(() => {
    if (!termsResponse || !(termsResponse as any).success) return "";
    const content = (termsResponse as any).data;
    if (typeof content === "string") return content;
    if (content?.html && typeof content.html === "string") return content.html;
    return "";
  }, [termsResponse]);

  const privacyHtml: string = useMemo(() => {
    if (!privacyResponse || !(privacyResponse as any).success) return "";
    const content = (privacyResponse as any).data;
    if (typeof content === "string") return content;
    if (content?.html && typeof content.html === "string") return content.html;
    return "";
  }, [privacyResponse]);
  const getText = (key: string) => t(`Documentation.${key}`);
  if (!data) return null;
  if (isLoading) {
    return (
      <div className="documentation-loading">
        <p>{getText("loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="documentation-error">
        <p>{getText("errorLoading")}</p>
        <p>
          {error instanceof Error
            ? error.message
            : "An unknown error occurred."}
        </p>
      </div>
    );
  }

  const handleTermsClick = () => setActiveTab("terms");
  const handlePrivacyClick = () => setActiveTab("privacy");

  return (
    <div className="documentation">
      <h1 className="documentation-title">{getText("documentation")}</h1>
      <section className="documentation-info">
        <div>
          <strong>{getText("versionNumber")}</strong>
          <p className="documentation-version">{data.version}</p>
        </div>
        <div>
          <strong>{getText("contactUs")}</strong>
          <p>
            <a
              href={`mailto:${data.contactEmail}`}
              className="documentation-contact"
            >
              {data.contactEmail}
            </a>
          </p>
        </div>
      </section>
      <section className="documentation-terms">
        <div className="tabs">
          <button
            className={`tab ${activeTab === "terms" ? "active" : ""}`}
            onClick={handleTermsClick}
          >
            {getText("Terms_and_Conditions")}
          </button>
          <button
            className={`tab ${activeTab === "privacy" ? "active" : ""}`}
            onClick={handlePrivacyClick}
          >
            {getText("Privacy_Policy")}
          </button>
        </div>
        <div className="tab-content">
          {activeTab === "terms" && (
            <div>
              {isLoadingTerms ? (
                <p>{getText("loading")}</p>
              ) : termsError ? (
                <p>{getText("errorLoading")}</p>
              ) : termsHtml ? (
                <iframe
                  title="terms-and-conditions"
                  className="html-iframe"
                  srcDoc={termsHtml}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                />
              ) : (
                <p>{getText("noContent") || "No content available"}</p>
              )}
            </div>
          )}
          {activeTab === "privacy" && (
            <div>
              {isLoadingPrivacy ? (
                <p>{getText("loading")}</p>
              ) : privacyError ? (
                <p>{getText("errorLoading")}</p>
              ) : privacyHtml ? (
                <iframe
                  title="privacy-policy"
                  className="html-iframe"
                  srcDoc={privacyHtml}
                  sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                />
              ) : (
                <p>{getText("noContent") || "No content available"}</p>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Documentation;
